import { useEffect, useState } from 'react';

// Lehká i18n bez závislostí. Konvence: KLÍČ = český text.
// cs → vrací klíč beze změny; en → překlad z mapy (fallback na klíč).
export type Lang = 'cs' | 'en';

const EN: Record<string, string> = {
  // Navigace – sekce
  'Práce': 'Work', 'Zdroje': 'Resources', 'Adresář': 'Directory', 'Systém': 'System',
  // Navigace – položky
  'Dashboard': 'Dashboard', 'Moje stanice': 'My station', 'Zakázky': 'Projects', 'Tabule': 'Board',
  'Harmonogram': 'Timeline', 'Plánování': 'Capacity', 'Reporty': 'Reports', 'Sklad': 'Inventory',
  // Řezy stránky Zakázky (přepínač pohledu + ⌘K)
  'Pohled na práci': 'Work view', 'Seznam': 'List', 'Kapacita': 'Capacity', 'Dílna': 'Workshop',
  'Zakázky — Seznam': 'Projects — List', 'Zakázky — Tabule': 'Projects — Board',
  'Zakázky — Harmonogram': 'Projects — Timeline', 'Zakázky — Kapacita': 'Projects — Capacity',
  'Zakázky — Dílna': 'Projects — Workshop',
  'Kde která výroba v dílně stojí — sloupce jsou výrobní fáze. Táhni kartu, jak práce postupuje.':
    'Where each build stands on the floor — columns are production phases. Drag a card as work moves on.',
  'Všechny zakázky se stavem, klientem a termínem.': 'All projects with status, client and deadline.',
  'Kanban fází napříč zakázkami — táhni kartičku mezi sloupci, klikni pro detail a komentáře.':
    'Kanban of phases across projects — drag a card between columns, click for detail and comments.',
  'Táhni fázi = posun, okraje = délka, lišta zakázky = posun všech fází, klik = otevřít.':
    'Drag a phase to move it, edges to resize, the project bar to shift all phases; click to open.',
  'Tabule práce v dílně — seskup podle fází, stavu nebo lidí a táhni karty, jak práce postupuje.':
    'Workshop board — group by phase, status or people and drag cards as work moves on.',
  'Týdenní vytížení lidí — zbývající práce z odhadů fází vs. kapacita podle směn a dovolených.':
    'Weekly workload — remaining work from phase estimates vs. capacity from shifts and leave.',
  'Knowledge base': 'Knowledge base', 'Graf propojení': 'Graph', 'Šablony': 'Templates',
  'Odběratelé': 'Customers', 'Departmenty': 'Departments',
  'Lidé': 'Team', 'Nastavení': 'Settings',
  // Navigace po zjednodušení (9 oblastí)
  'Každodenní práce': 'Daily work', 'Provoz': 'Operations', 'Řízení': 'Management',
  'Přehled': 'Overview', 'Moje práce': 'My work', 'Kontakty': 'Contacts', 'Správa': 'Admin',
  'Půjčovna': 'Rental', 'Koordinace': 'Coordination', 'Materiál': 'Materials', 'Inventura': 'Stocktake',
  'Docházka': 'Attendance', 'Skenovat': 'Scan', 'AI asistent': 'AI assistant',
  'Položky k pronájmu': 'Rental items', 'Rezervace': 'Bookings', 'Poptávky': 'Inquiries', 'Vytížení': 'Utilisation',
  'Nabídky dodavatelů': 'Supplier quotes', 'Dodavatelé': 'Suppliers', 'Zákazníci dodavatelů': 'Supplier customers',
  // Chrome
  'Hledat nebo skočit…': 'Search or jump…', 'Odhlásit': 'Log out', 'Notifikace': 'Notifications',
  'Světlý režim': 'Light mode', 'Tmavý režim': 'Dark mode', 'Menu': 'Menu',
  // Login
  'Přihlášení do interní aplikace': 'Sign in to your workspace', 'E-mail': 'Email', 'Heslo': 'Password',
  'Přihlašuji…': 'Signing in…', 'Přihlásit se': 'Sign in',
  // Dashboard
  'Přehled aktivních zakázek, termínů a skladu.': 'Overview of active projects, deadlines and inventory.',
};

let lang: Lang = (typeof localStorage !== 'undefined' && (localStorage.getItem('lang') as Lang)) || 'cs';
if (typeof document !== 'undefined') document.documentElement.lang = lang;
const listeners = new Set<() => void>();

export const getLang = (): Lang => lang;
export function setLang(l: Lang): void {
  lang = l;
  if (typeof localStorage !== 'undefined') localStorage.setItem('lang', l);
  if (typeof document !== 'undefined') document.documentElement.lang = l;
  listeners.forEach((fn) => fn());
}

// Překlad. cs → klíč; en → z mapy nebo fallback na klíč.
export const t = (cs: string): string => (lang === 'en' ? EN[cs] ?? cs : cs);

export function useT(): (cs: string) => string {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((x) => x + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return t;
}

export function useLang(): [Lang, (l: Lang) => void] {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((x) => x + 1);
    listeners.add(fn);
    return () => { listeners.delete(fn); };
  }, []);
  return [lang, setLang];
}
